import React from 'react';
import { DialogNext as Dialog } from 'ha/components/Dialog';
import { Grid, Card, CardContent, Typography, Divider, units } from '@hbf/dsl';
import { TextTooltip } from 'ha/components.legacy/TextTooltip';
import { RentalConditions } from '@ha/api/v2/types/RentalConditions';
import { useIntl } from 'ha/i18n';
import { makeStyles } from 'tss-react/mui';
import { Listing } from '@ha/api/v2/types/Listing';
import {
  FeeMode,
  getPricingStructure,
  PricingStructure,
} from 'ha/modules/Pricing';
import { UnitType } from '@ha/api/v2/types/inventory/UnitType';

import { RentInfo } from 'ha/modules/PaymentDialog/RentInfo';
import { getPaymentSections } from './getPaymentSections';
import { ContractType } from './ContractType';

const useStyles = makeStyles()(theme => ({
  crossedOutPrice: {
    color: theme.palette.grey[500],
    textDecoration: 'line-through',
    marginLeft: theme.spacing(1),
  },
  cardcontent: {
    padding: units.rem(1),
    '&:last-child': {
      paddingBottom: units.rem(1),
    },
  },
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  listing: Listing | UnitType;
  rentalConditions?: RentalConditions;
  feeMode: FeeMode;
  dialogActions?: JSX.Element;
  isAdvertiserVariant?: boolean;
}

const PaymentDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  listing,
  rentalConditions,
  feeMode,
  dialogActions,
  isAdvertiserVariant,
}) => {
  const { classes } = useStyles();
  const { T, formatDate, formatCurrency } = useIntl();

  const sections = getPaymentSections({
    listing,
    rentalConditions,
    feeMode,
    isAdvertiserVariant,
    formatDate,
    formatCurrency,
    T,
  });

  const contractType = rentalConditions?.contractType || listing.contractType;
  const pricingStructure = getPricingStructure({ rentalConditions });

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      dividers
      name="Payment Overview"
      maxWidth="sm"
      fullWidth
      title={
        isAdvertiserVariant
          ? T('payment_overview.advertiser.title')
          : T('paymentOverview.dialog_title')
      }
      content={
        <Grid container spacing={2} data-test-locator="PaymentDialog">
          {sections.map(({ key, title, rows, total, dueOn }) => {
            if (!rows.length) return null;
            return (
              <React.Fragment key={title.text}>
                {key === 'instalments' &&
                  pricingStructure === PricingStructure.instalment && (
                    <ContractType
                      contractType={contractType}
                      isAdvertiserVariant={isAdvertiserVariant}
                    />
                  )}
                <Grid
                  item
                  xs={12}
                  data-test-locator={`PaymentDialog/${title.text}`}
                  key={title.text}
                >
                  <Card variant="outlined">
                    <CardContent className={classes.cardcontent}>
                      <Grid
                        item
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Grid item xs={12}>
                          <Typography.Paragraph
                            fontWeight="bold"
                            textTransform="uppercase"
                          >
                            {title.text}
                            {title.tooltip ? (
                              <TextTooltip tooltip={title.tooltip} />
                            ) : null}
                          </Typography.Paragraph>
                        </Grid>
                        {key === 'instalments' && (
                          <Grid item xs={12}>
                            <RentInfo
                              contractType={contractType}
                              isAdvertiserVariant={isAdvertiserVariant}
                            />
                          </Grid>
                        )}
                        {rows.map(row => (
                          <React.Fragment key={row.caption}>
                            <Grid item xs={row.date ? 12 : 8}>
                              <Typography.Paragraph>
                                {row.caption}
                                {row.tooltip ? (
                                  <TextTooltip tooltip={row.tooltip} />
                                ) : null}
                              </Typography.Paragraph>
                            </Grid>
                            <Grid
                              item
                              xs={row.date ? 6 : 4}
                              data-test-locator={`PaymentDialog/${title.text}/${row.caption}`}
                            >
                              <Typography.Paragraph
                                align={row.date ? 'left' : 'right'}
                              >
                                {row.date || row.value}
                                {!!row.crossedPrice && (
                                  <span className={classes.crossedOutPrice}>
                                    {row.crossedPrice}
                                  </span>
                                )}
                              </Typography.Paragraph>
                            </Grid>
                            {row.date && (
                              <Grid
                                item
                                xs={6}
                                data-test-locator={`PaymentDialog/${row.caption}/value`}
                              >
                                <Typography.Paragraph align="right">
                                  {row.value}
                                </Typography.Paragraph>
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                        {total && (
                          <React.Fragment>
                            <Divider />
                            <Grid item xs={6}>
                              <Typography.Paragraph fontWeight="bold">
                                {key === 'instalments'
                                  ? T('payment_overview.rent.total')
                                  : T('paymentOverview.total')}
                              </Typography.Paragraph>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography.Paragraph
                                align="right"
                                fontWeight="bold"
                                data-test-locator={`PaymentDialog/${title.text}/total`}
                              >
                                {total}
                              </Typography.Paragraph>
                            </Grid>
                          </React.Fragment>
                        )}
                        {dueOn ? (
                          <Grid
                            item
                            xs={12}
                            data-test-locator={`PaymentDialog/${title.text}/dueOn`}
                          >
                            <Typography.Paragraph>{dueOn}</Typography.Paragraph>
                          </Grid>
                        ) : null}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      }
      dialogActions={dialogActions}
    />
  );
};

export { PaymentDialog };
